import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { ShippingRecordItem, ShippingRecordDestination, ShippingDestinationDetailItem, ShippingRecordHarvestRecord, SelectAgriFieldAndPlanting, ShippingDestination, QualityStandard, WeightUnit, RecordInfoModel } from "@/types/Component";
import { ShippingRecordResponse, QualityStandardResponse, WeightUnitResponse } from "@/types/APIResponse"
import moment from 'moment';
import SelectFromHarvestRecord from '@/components/Record/SelectFromHarvestRecord/SelectFromHarvestRecord.vue';
import SelectFromPlantings from '@/components/Record/SelectFromPlantings/SelectFromPlantings.vue'
import RecordInfo from '@/components/Record/RecordInfo/RecordInfo.vue'
import ShippingDestinationSelectModal from "@/assets/common/Modal/ItemSelectModal/ShippingDestinationSelectModal/ShippingDestinationSelectModal.vue";
import EditModal from '@/assets/common/Modal/EditModal/EditModal.vue';
import { toCamel } from 'snake-camel';
import store from '@/assets/common/store/store';

type ShippingRecordPostModel = {
  shipping_date: string;
  shipping_type: string;
  harvest_record_id: string[] | null;
  planting_id: string | null;
  agri_field_id: string | null;
  shipping_destinations: {
    shipping_destination_id: string;
    shipping_destination_details: {
      quality_standard_id: string | null;
      quantity: number | null;
      weight_unit_id: string | null;
      amount: number | null;
      unit_price: number | null;
      memo: string;
    }[];
  }[];
  lot_number: string;
  memo: string;
}

type QualityStandards = {
  qualityStandards: QualityStandard[];
}

type WeightUnits = {
  weightUnits: WeightUnit[];
}

@Component({
  components: {
    SelectFromHarvestRecord,
    SelectFromPlantings,
    ShippingDestinationSelectModal,
    EditModal,
    RecordInfo
  }
})
export default class ShippingRecord extends Vue {
  @Prop()
  private id?: string

  @Prop({ default: ''})
  private recordDate!: string

  @Prop({ default: false})
  private isAdmin!: boolean

  private mode = ''
  private apiBaseUrl = '/admin/shipping_records'
  private today = moment(new Date()).format('YYYY-MM-DD')
  private defaultShippingRecord: ShippingRecordItem = {
    id: "",
    shippingDate: this.recordDate? this.recordDate : this.today,
    shippingType: "",
    classification:"",
    harvestRecords: null,
    harvestRecordData:[],
    measurementTimingId:"",
    workRecordId:"",
    planting: null,
    agriField: null,
    shippingDestinations: [],
    workers: [],
    machines: [],
    lotNumber: "",
    memo: "",
    workRecordOption: "",
    createdUser: {
      id: "",
      name: "",
      imageId: ""
    },
    created: this.today,
    updatedUser: {
      id: "",
      name: "",
      imageId: ""
    },
    updated: this.today
  }
  private shippingRecord: ShippingRecordItem = this.defaultShippingRecord
  private qualityStandards: QualityStandard[] = []
  private weightUnits: WeightUnit[] = []
  private defaultWeightUnit: WeightUnit = {
    id: null,
    name: 'kg',
    weightPerUnit: 1,
    isHidden: false
  }

  private defaultDestinationDetail: ShippingDestinationDetailItem = {
    shippingRecordDestinationId:'',
    harvestQualityStandardId: null,
    qualityStandardId: null,
    qualityStandardName: '',
    quantity: null,
    weightUnitId: null,
    weightUnitName: this.defaultWeightUnit.name,
    amount: null,
    unitPrice: null,
    memo: '',
    selectedWeightUnit: this.defaultWeightUnit
  }
  private isAutoCalc = true

  // 出荷対象の選択状態管理
  private selectedShippingTarget: {
    harvestRecordId: string[];
    agriFieldId: string | null;
    plantingId: string | null;
  } = {
    harvestRecordId: [],
    agriFieldId: null,
    plantingId: null
  }

  // 収穫記録から選択モーダル
  private showSelectFromHarvestRecord = false
  private selectedHarvestRecords: ShippingRecordHarvestRecord[] = []

  // 作付から選択モーダル
  private showSelectFromPlantings = false

  // 出荷先選択モーダル
  private showDestinationModal = false
  private selectedDestinations: ShippingDestination[] = []

  // 編集モーダル
  private showEditModal = false
  private editModalBaseUrl = ''

  // 作成者/更新者
  private recordInfo: RecordInfoModel | null = null

  async created() {
    this.init()
  }

  async init() {
    this.permissionCheck()
    await this.getQualityStandards()
    await this.getWeightUnits()
    if(this.id) {
      this.mode = 'edit'
      await this.getShippingRecord(this.id)
    } else {
      this.shippingRecord = Object.assign({}, this.defaultShippingRecord)
    }
  }

  async getShippingRecord(id: string) {
    return this.$apiClient.doRequest('get', `${this.apiBaseUrl}/${id}`)
    .then((res: ShippingRecordResponse)=>{
      const camelRes = toCamel(res) as ShippingRecordItem
      this.shippingRecord = camelRes
      this.shippingRecord.harvestRecords = camelRes.harvestRecords?.map(record => {
        return {
          id: record.id,
          harvestDate: record.harvestDate,
          planting: camelRes.planting,
          agriField: camelRes.agriField
        }
      }) as ShippingRecordHarvestRecord[]
      this.shippingRecord.shippingDestinations.forEach((destination => {
        destination.shippingDestinationDetails = destination.shippingDestinationDetails.map(item => {
          return {
            ...item,
            selectedWeightUnit: item.weightUnitId ? this.getWeightUnit(item.weightUnitId) : this.defaultWeightUnit
          }
        })
      }))
      this.recordInfo = {
        createdUser: this.shippingRecord.createdUser,
        created: this.shippingRecord.created,
        updatedUser: this.shippingRecord.updatedUser,
        updated: this.shippingRecord.updated
      }
    })
    .catch(()=>{
      this.$toasted.error('データの取得に失敗しました。')
    })
  }

  async getQualityStandards() {
    return this.$apiClient.doRequest('get', '/quality_standards?with_hidden=true')
    .then((res: QualityStandardResponse)=>{
      this.qualityStandards = (toCamel(res) as QualityStandards).qualityStandards
    })
    .catch(()=>{
      this.$toasted.error('データの取得に失敗しました。')
    })
  }

  async getWeightUnits() {
    return this.$apiClient.doRequest('get', '/weight_units?with_hidden=true')
    .then((res: WeightUnitResponse)=>{
      this.weightUnits = (toCamel(res) as WeightUnits).weightUnits
    })
    .catch(()=>{
      this.$toasted.error('データの取得に失敗しました。')
    })
  }

  permissionCheck() {
    // 管理者以外はトップページへリダイレクト
    if(!this.isAdmin) {
      this.$router.push('/', () => {return})
    }
  }

  deleteHarvestRecord(index: number) {
    this.shippingRecord.harvestRecords?.splice(index, 1)
  }

  deletePlantingRecord() {
    this.shippingRecord.planting = null
    this.shippingRecord.agriField = null
  }

  deleteDestination(index: number) {
    this.shippingRecord.shippingDestinations?.splice(index, 1)
  }

  addDestinationDetail(sd:ShippingRecordDestination) {
    sd.shippingDestinationDetails.push(Object.assign({}, this.defaultDestinationDetail))
    this.inputChanged()
  }

  deleteDestinationDetail(sd:ShippingRecordDestination, index: number) {
    sd.shippingDestinationDetails.splice(index, 1)
    this.inputChanged()
  }

  calcAmount(detail: ShippingDestinationDetailItem) {
    if(!this.isAutoCalc) {
      return false
    }
    if(!detail.quantity || !detail.unitPrice) {
      detail.amount = null
    } else {
      const calc = detail.quantity * detail.unitPrice
      detail.amount = Math.round(calc)
    }
    this.inputChanged()
  }

  calcUnitPrice(detail: ShippingDestinationDetailItem) {
    if(!this.isAutoCalc) {
      return false
    }
    if(!detail.quantity || detail.quantity == 0 || !detail.amount) {
      detail.unitPrice = null
    } else {
      const calc = detail.amount / detail.quantity
      detail.unitPrice = Math.round(calc * 100) / 100
    }
    this.inputChanged()
  }

  calcQuantityPerWeight(detail: ShippingDestinationDetailItem) {
    if(!detail.quantity || !detail.selectedWeightUnit?.id || !detail.selectedWeightUnit?.weightPerUnit) {
      return ''
    }
    const calc = detail.quantity * detail.selectedWeightUnit.weightPerUnit
    return Math.round(calc * 1000) / 1000
  }

  calcAmountPerWeight(detail: ShippingDestinationDetailItem) {
    if(!detail.amount || !detail.selectedWeightUnit?.id || !detail.selectedWeightUnit?.weightPerUnit) {
      return ''
    }
    const quantity = this.calcQuantityPerWeight(detail)
    const calc = detail.amount / Number(quantity)
    return Math.round(calc * 100) / 100
  }

  getWeightUnit(id: string) {
    if(!id) {
      return this.defaultWeightUnit
    }
    const unit = this.weightUnits.find(unit => unit.id == id)
    return unit ? unit : this.defaultWeightUnit
  }

  isWeightPerUnit(detail: ShippingDestinationDetailItem) {
    return detail.quantity && detail.selectedWeightUnit?.id && detail.selectedWeightUnit.weightPerUnit
  }

  // 収穫記録から選択モーダル
  openHarvestModal() {
    this.selectedShippingTarget = {
      harvestRecordId: this.shippingRecord.harvestRecords?
          this.shippingRecord.harvestRecords.map(record => record.id) : [],
      agriFieldId: this.shippingRecord.agriField?.id ?? null,
      plantingId: this.shippingRecord.planting?.id ?? null
    }
    this.showSelectFromHarvestRecord = true
  }
  // 収穫記録選択モーダルからのコールバック
  commitHarvestRecords(harvestRecords: ShippingRecordHarvestRecord[]) {
    this.shippingRecord.harvestRecords = harvestRecords
    this.shippingRecord.shippingType = 'HARVEST_RECORD'
    this.showSelectFromHarvestRecord = false
    this.shippingRecord.planting = harvestRecords[0].planting
    this.shippingRecord.agriField = harvestRecords[0].agriField
  }

  // 作付選択モーダル表示
  openPlantingModal() {
    this.selectedShippingTarget.agriFieldId = this.shippingRecord.agriField? this.shippingRecord.agriField.id : null
    this.selectedShippingTarget.plantingId = this.shippingRecord.planting? this.shippingRecord.planting.id : null
    this.showSelectFromPlantings = true
  }

  // 作付選択モーダルからのコールバック
  commitPlantingAndField(selectedItem: SelectAgriFieldAndPlanting) {
    this.shippingRecord.planting = {
      id: selectedItem.planting.id,
      name: selectedItem.planting.name
    }
    this.shippingRecord.agriField = {
      id: selectedItem.agriField.id,
      name: selectedItem.agriField.name
    }
    this.shippingRecord.harvestRecords = null
    this.shippingRecord.shippingType = 'PLANTING'
    this.showSelectFromPlantings = false

  }

  // 出荷先選択モーダル表示
  openDestinationModal() {
    this.selectedDestinations = this.shippingRecord.shippingDestinations.map(destination => {
      return {
        id: destination.id,
        name: destination.name,
        isHidden: false
      }
    })
    this.showDestinationModal = true
  }

  // 出荷先選択モーダルからのコールバック
  commitDestinations(shippingDestinations: ShippingDestination[]) {
    let destinations = this.shippingRecord.shippingDestinations
    // チェックが外れたものを取り除く
    destinations = destinations.filter(sd => {
      return shippingDestinations.some(item => item.id == sd.id)
    })
    // モーダルから返却されたもので更新する
    this.shippingRecord.shippingDestinations = shippingDestinations.map(sd => {
      const shippingDestinationDetails = destinations.find(destination => destination.id === sd.id)?.shippingDestinationDetails
      const newShippingDestinationDetails = [Object.assign({}, this.defaultDestinationDetail)]
        return  {
          id: sd.id,
          name: sd.name,
          shippingDestinationDetails: shippingDestinationDetails ? shippingDestinationDetails : newShippingDestinationDetails
        }
    })
  }

  // 出荷先選択モーダルで出荷先が更新される
  updateShippingDestination(shippingDestination: ShippingDestination) {
    if (this.shippingRecord.shippingDestinations.length) {
      this.shippingRecord.shippingDestinations.forEach(item => {
        if (item.id === shippingDestination.id) {
          item.name = shippingDestination.name
        }
      })
    }
  }

  // 編集モーダル表示
  private editWeightUnits() {
    this.editModalBaseUrl = '/weight_units'
    this.showEditModal = true
  }

  private editQualityStandards() {
    this.editModalBaseUrl = '/quality_standards'
    this.showEditModal = true
  }

  // 編集モーダルからのコールバック
  private async updateItems(baseUrl: string) {
    switch (baseUrl) {
      case '/weight_units':
        this.getWeightUnits().then(() => {
          for(const shippingDestination of this.shippingRecord.shippingDestinations) {
            shippingDestination.shippingDestinationDetails.map(detail => {
              const selectedWeightUnit = this.weightUnits.find(weightUnit => weightUnit.id == detail.selectedWeightUnit?.id)
              detail.selectedWeightUnit = selectedWeightUnit ? selectedWeightUnit : this.defaultWeightUnit
            })
          }
        })
        break
      case '/quality_standards':
        await this.getQualityStandards()
        break
    }
  }

  // 編集モーダルのクローズ
  private closeEditModal() {
    this.showEditModal = false
  }

  // 入力チェック
  validation(): boolean {
    const errmsg: string[] = []

    // 出荷日
    if(!this.shippingRecord.shippingDate) {
      errmsg.push('出荷日を選択してください')
    }

    // 出荷対象
    if(this.shippingRecord.shippingType == 'HARVEST_RECORD') {
      if(this.shippingRecord.harvestRecords == null || this.shippingRecord.harvestRecords.length == 0) {
        errmsg.push('出荷対象を選択してください')
      }
    } else {
      if(this.shippingRecord.planting == null || this.shippingRecord.agriField == null) {
        errmsg.push('出荷対象を選択してください')
      }
    }

    // 出荷先
    if(this.shippingRecord.shippingDestinations == null || this.shippingRecord.shippingDestinations.length == 0) {
      errmsg.push('出荷先を選択してください')
    } else {
      this.shippingRecord.shippingDestinations.forEach(destination => {
        if(destination.shippingDestinationDetails.length == 0) {
          errmsg.push(`明細が入力されていません(出荷先：${destination.name})`)
        }
        destination.shippingDestinationDetails.forEach(detail => {
          if(detail.qualityStandardId === "" || detail.qualityStandardId === null) {
            errmsg.push(`品質・規格を選択してください(出荷先：${destination.name})`)
          }
          if(detail.quantity == null) {
            errmsg.push(`明細に数量を入力してください(出荷先：${destination.name})`)
          } else {
            const quantityStr = detail.quantity.toString().split('.')
            if(detail.quantity < 0 || quantityStr[0].length > 7 || (quantityStr[1] && quantityStr[1].length > 2)) {
              errmsg.push(`数量は0以上の整数部7桁小数部2桁までの数値を入力してください(出荷先：${destination.name})`)
            }
          }
          if(detail.amount && (detail.amount < 0 || !Number.isInteger(Number(detail.amount)))) {
            errmsg.push(`金額は0以上の整数を入力してください(出荷先：${destination.name})`)
          }
          if(detail.unitPrice) {
            const unitStr = detail.unitPrice.toString().split('.')
            if(detail.unitPrice < 0 || unitStr[0].length > 7 || (unitStr[1] && unitStr[1].length > 2)) {
              errmsg.push(`単価は0以上の整数部7桁小数部2桁までの数値を入力してください(出荷先：${destination.name})`)
            }
          }
          if(detail.memo && detail.memo.length > 21844) {
            errmsg.push(`備考は21844文字以内で入力してください（現在は${detail.memo.length}文字, 出荷先：${destination.name}）`)
          }
        })
      })
    }
    // メモ
    if(this.shippingRecord.memo && this.shippingRecord.memo.length > 21844) {
      errmsg.push(`メモは21844文字以内で入力してください（現在は${this.shippingRecord.memo.length}文字）`)
    }

    if(errmsg.length > 0) {
      window.alert(errmsg.join('\n'))
      return false
    }

    return true
  }

  // refsで呼ばれるフッターボタン
  async saveRecord() {
    if(!this.validation()) {
      return false
    }

    const shippingDestinations = this.shippingRecord.shippingDestinations.map(destination => {
      const details = destination.shippingDestinationDetails.map(detail => {
        return {
          quality_standard_id: detail.qualityStandardId,
          quantity: detail.quantity,
          weight_unit_id: (detail.selectedWeightUnit?.id || detail.selectedWeightUnit?.id === null) ? detail.selectedWeightUnit.id : detail.weightUnitId,
          amount: detail.amount,
          unit_price: detail.unitPrice,
          memo: detail.memo
        }
      })
      return {
        shipping_destination_id: destination.id,
        shipping_destination_details: details
      }
    })

    const data:ShippingRecordPostModel = {
      shipping_date: this.shippingRecord.shippingDate,
      shipping_type: this.shippingRecord.shippingType,
      harvest_record_id: this.shippingRecord.harvestRecords && this.shippingRecord.harvestRecords.length > 0?
        this.shippingRecord.harvestRecords.map(record => record.id) : null,
      planting_id: this.shippingRecord.planting?.id ?? null,
      agri_field_id: this.shippingRecord.agriField?.id ?? null,
      shipping_destinations: shippingDestinations,
      lot_number: this.shippingRecord.lotNumber,
      memo: this.shippingRecord.memo
    }

    let method: "get" | "post" | "put" | "delete" = 'post'
    let url = this.apiBaseUrl
    let message = '保存しました。'

    if(this.mode == 'edit') {
      method = 'put'
      url = `${this.apiBaseUrl}/${this.id}`
      message = '変更を保存しました。'
      if(!window.confirm("内容を上書きして保存します。よろしいですか。")) {
        return false
      }
    }

    return this.$apiClient.doRequest(method, url, data)
    .then((res)=>{
      this.$toasted.success(message)
      if(this.$route.query.return_to){
        this.$store.dispatch('doUpdateIsInputChanged', false);
        this.$router.push(`/${this.$route.query.return_to}?date=${this.shippingRecord.shippingDate}`, () => {return})
      }else {
        this.init()
        if(this.id) {
            this.getShippingRecord(this.id)
        }
      }
    })
    .catch(()=>{
      this.$toasted.error('保存に失敗しました。')
    })
  }

  async deleteRecord() {
    const confirm = window.confirm('削除しますか？')
    if(confirm) {
      return this.$apiClient.doRequest('delete', `${this.apiBaseUrl}/${this.id}`)
      .then((res)=>{
        this.$toasted.success('削除しました。')
        this.$router.push('/calendar', () => {return})
      })
      .catch(()=>{
        this.$toasted.error('削除に失敗しました。')
      })
    } else {
      this.$toasted.success('削除がキャンセルされました。')
    }

  }

  private filterWeightUnitsByDetail(detail: ShippingDestinationDetailItem) {
    return this.weightUnits
      .filter(wu => (detail.selectedWeightUnit?.id == wu.id || detail.weightUnitId == wu.id) || wu.isHidden === false)
  }

  private filterQualityStandardsByDetail(detail: ShippingDestinationDetailItem) {
    return this.qualityStandards
      .filter(qs => detail.qualityStandardId == qs.id || qs.isHidden === false)
  }

  @Emit('inputChanged')
  private inputChanged(){
    if(store.getters.isInputChanged==false){
    this.$store.dispatch('doUpdateIsInputChanged', true);
    }
  }

  cancelRecord(): void {
    const result = window.confirm('変更内容を破棄します。よろしいですか。')
    if (result) {
      if (this.id !== undefined && this.id !== ''){
        this.getShippingRecord(this.id);
      }else{
        this.init()
      }
    }
    this.$store.dispatch('doUpdateIsInputChanged', false);
  }

}